import { AiDrawer, AliOssImage } from '@aigc/components';
import { useOverlay, useOverlayRef } from '@aigc/overlay-hook';
import { ReloadOutlined } from '@ant-design/icons';
import { useLens } from '@hookform/lenses';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Descriptions, Empty, Image, Skeleton } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { array, object, string, type ObjectSchema } from 'yup';
import fallbackImage from '../../common/fallback.png';
import { getSkillsQueryOptions } from '../../skill/api';
import {
  getBaseTaskListQueryKey,
  getOneTaskQueryOptions,
} from '../../task/api';
import { TaskStatus } from '../../task/components/task-status';
import { TaskStatusCode, type ProductAnalysisTaskDto } from '../../task/model';
import { ScriptSource } from '../../video/model';
import { buildPrompt } from '../../video/utils';
import { analyzeProductMutationOptions } from '../api';
import type { ProductScriptFormValues } from '../model';
import {
  useProductAnalysisResultDraft,
  useResetProductAnalysisResultDraft,
} from '../store';
import {
  appendProductShotImageTask,
  removeProductShotImageTask,
} from '../utils';
import { ProductScriptTable } from './product-script-table';
import { ProductShotImageDrawer } from './product-shot-image-drawer';

const product_script_schema: ObjectSchema<ProductScriptFormValues> = object({
  shots: array()
    .of(
      object({
        script: string().trim().required('请输入脚本'),
        image_instructions: string().trim().required('请输入生图指令'),
      }),
    )
    .required(),
});

export type ProductAnalysisDrawerCloseReason = 'manual' | 'navigate';

export function ProductAnalysisDrawer(props: {
  taskId: string;
  onTaskIdChange: (task_id: string) => void;
}) {
  const { message } = App.useApp();
  const overlay_ref = useOverlayRef<ProductAnalysisDrawerCloseReason>();
  const [open_shot_image_drawer] = useOverlay<boolean>();
  const query_client = useQueryClient();
  const [current_task_id, set_current_task_id] = useState(props.taskId);
  const [draft, set_draft] = useProductAnalysisResultDraft(current_task_id);
  const reset_draft = useResetProductAnalysisResultDraft(current_task_id);
  const initialized_task_id_ref = useRef<string | undefined>(undefined);
  const retry_mutation = useMutation(analyzeProductMutationOptions);
  const script_form = useForm<ProductScriptFormValues>({
    defaultValues: { shots: [] },
    resolver: yupResolver(product_script_schema),
  });
  const { reset: reset_script_form } = script_form;
  const script_lens = useLens({ control: script_form.control });
  const task_query = useQuery({
    ...getOneTaskQueryOptions<ProductAnalysisTaskDto>(current_task_id),
    refetchInterval: (query) =>
      TaskStatus.isSettled(query.state.data?.status) ? false : 5000,
  });
  const skills_query = useQuery(getSkillsQueryOptions({ page: 1, size: 100 }));
  const task = task_query.data;
  const input = task?.generate_data;
  const result = task?.result;

  useEffect(() => {
    if (initialized_task_id_ref.current === current_task_id) {
      return;
    }

    if (draft.form_values) {
      reset_script_form(draft.form_values);
      initialized_task_id_ref.current = current_task_id;
      return;
    }

    if (result) {
      const form_values: ProductScriptFormValues = {
        shots: result.shots.map((shot, index) => ({
          script: buildPrompt(shot, index),
          image_instructions: shot.image_instructions,
        })),
      };

      reset_script_form(form_values);
      set_draft((current) => ({ ...current, form_values }));
      initialized_task_id_ref.current = current_task_id;
    }
  }, [
    current_task_id,
    draft.form_values,
    reset_script_form,
    result,
    set_draft,
  ]);

  useEffect(() => {
    return script_form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        set_draft((current) => ({
          ...current,
          form_values: values as ProductScriptFormValues,
        }));
      },
    });
  }, [script_form, set_draft]);

  useEffect(() => {
    if (TaskStatus.isSettled(task?.status)) {
      query_client.invalidateQueries({
        queryKey: getBaseTaskListQueryKey(),
      });
    }
  }, [query_client, task?.status]);

  const skill_name = useMemo(() => {
    if (input?.skill_id == null) {
      return '-';
    }

    return (
      skills_query.data?.items.find((skill) => skill.id === input.skill_id)
        ?.name ?? `技能 #${input.skill_id}`
    );
  }, [input?.skill_id, skills_query.data?.items]);

  const handleRetry = async () => {
    if (!input || retry_mutation.isPending) {
      return;
    }

    const new_task = await retry_mutation.mutateAsync(input);
    await query_client.invalidateQueries({
      queryKey: getBaseTaskListQueryKey(),
    });
    set_current_task_id(new_task.id);
    props.onTaskIdChange(new_task.id);
    message.success('爆款生成任务已重新创建');
  };

  return (
    <AiDrawer
      title={`爆款生成 #${current_task_id}`}
      size="70%"
      mask={{ closable: false }}
      onClose={() => {
        reset_draft();
        overlay_ref.close('manual');
      }}
      footer={
        TaskStatus.isSucceeded(task?.status) && (
          <div className="flex">
            <div className="flex-auto"></div>
            <Button
              variant="solid"
              color="primary"
              disabled={!result?.shots.length || !input}
              onClick={script_form.handleSubmit(async (form_values) => {
                if (!result || !input) {
                  return;
                }

                const overlayRef = open_shot_image_drawer(
                  <ProductShotImageDrawer
                    source={ScriptSource.爆款生成}
                    productName={result.product_name}
                    productImages={input.product_images}
                    shots={result.shots}
                    formValues={form_values}
                    createdTasks={draft.image_tasks ?? []}
                    imageGenerationParams={draft.image_generation_params ?? {}}
                    onImageGenerationParamsChange={(shot_position, params) => {
                      set_draft((current) => ({
                        ...current,
                        image_generation_params: {
                          ...current.image_generation_params,
                          [shot_position]: params,
                        },
                      }));
                    }}
                    onImageInstructionsChange={(
                      shot_position,
                      image_instructions,
                    ) => {
                      script_form.setValue(
                        `shots.${shot_position}.image_instructions`,
                        image_instructions,
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        },
                      );
                    }}
                    onTaskCreated={(shot_position, created_task) => {
                      set_draft((current) => ({
                        ...current,
                        image_tasks: appendProductShotImageTask(
                          current.image_tasks ?? [],
                          {
                            shot_position,
                            initial_task: created_task,
                          },
                        ),
                      }));
                    }}
                    onTaskRemoved={(task_id) => {
                      set_draft((current) => ({
                        ...current,
                        image_tasks: removeProductShotImageTask(
                          current.image_tasks ?? [],
                          task_id,
                        ),
                      }));
                    }}
                  />,
                );

                const shouldClose = await overlayRef.afterClosed;

                if (shouldClose) {
                  overlay_ref.close('navigate');
                }
              })}
            >
              去生成图片
            </Button>
          </div>
        )
      }
    >
      {task_query.isLoading ? (
        <Skeleton active />
      ) : task_query.isError || !task ? (
        <Alert
          type="error"
          showIcon
          title="任务详情加载失败"
          description="请检查网络连接后重试"
          action={
            <Button
              icon={<ReloadOutlined />}
              loading={task_query.isFetching}
              onClick={() => task_query.refetch()}
            >
              重新加载
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <TaskStatus task={task} />
            {!TaskStatus.isSettled(task.status) && (
              <span className="text-sm text-primary-500">
                任务进行中，请稍候。你也可以放心关闭弹窗，稍后回来再点击任务查看结果。
              </span>
            )}
          </div>

          <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="产品名称">
              {input?.product_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="国家/地区">
              {input?.region || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="技能">{skill_name}</Descriptions.Item>
            <Descriptions.Item label="时长">
              {input?.duration == null ? '-' : `${input.duration} 秒`}
            </Descriptions.Item>
            <Descriptions.Item label="产品图" span={2}>
              {input?.product_images.length ? (
                <Image.PreviewGroup>
                  <div className="flex flex-wrap gap-3">
                    {input.product_images.map((image, index) => (
                      <AliOssImage
                        key={`(${index})${image.name}`}
                        classNames={{ image: 'rounded object-cover' }}
                        size={96}
                        src={image.url}
                        fallback={fallbackImage}
                        alt={`产品图 ${image.name}`}
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="额外要求" span={2}>
              <span className="whitespace-pre-wrap">
                {input?.extra_prompt || '-'}
              </span>
            </Descriptions.Item>
          </Descriptions>

          {task.status === TaskStatusCode.任务失败 ? (
            <div className="space-y-4">
              <Alert
                type="error"
                showIcon
                title="爆款生成失败"
                description={task.error || '任务执行失败，请重试'}
              />
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={retry_mutation.isPending}
                disabled={!input}
                onClick={handleRetry}
              >
                重试
              </Button>
              {!input && (
                <span className="ml-3 text-sm text-red-500">
                  原始任务参数不可用，无法重试
                </span>
              )}
            </div>
          ) : task.status === TaskStatusCode.任务成功 ? (
            result ? (
              <div className="border-t border-primary-200 pt-4">
                {result.shots.length ? (
                  <ProductScriptTable
                    shots={result.shots}
                    lens={script_lens.focus('shots')}
                    productImages={input?.product_images ?? []}
                  />
                ) : (
                  <Empty description="暂无解析结果" />
                )}
              </div>
            ) : (
              <Empty description="爆款生成结果为空" />
            )
          ) : null}
        </div>
      )}
    </AiDrawer>
  );
}
